import React, { useEffect, useRef, useState } from "react";
import { Activity, Heart, Footprints, Bluetooth, Camera, Plus, AlertTriangle, Loader2, Droplets, Thermometer, Scale, Gauge, Moon, Smile, X, Trophy, Wind, Sparkles, Image as ImageIcon, TrendingUp, Medal } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Guardian from "@/components/Guardian";

const fmtTime = (iso) => { try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const TWIN_IMG = "https://static.prod-images.emergentagent.com/jobs/6dfaf7ec-672f-421f-93af-1927e0557ba9/images/2e6c09a840797512ddcb264ccf0677ceefeee0b4edf258025c4afbb6a5e52466.jpeg";

// ---- Live step counter via the phone accelerometer -------------------------
function useStepCounter() {
  const [running, setRunning] = useState(false);
  const [steps, setSteps] = useState(0);
  const [motion, setMotion] = useState("—");
  const st = useRef({ last: 0, avg: 0, lastPeak: 0, window: [] });

  useEffect(() => {
    if (!running) return;
    const onMotion = (e) => {
      const a = e.accelerationIncludingGravity || e.acceleration;
      if (!a) return;
      const mag = Math.sqrt((a.x || 0) ** 2 + (a.y || 0) ** 2 + (a.z || 0) ** 2);
      const s = st.current;
      s.avg = s.avg ? s.avg * 0.9 + mag * 0.1 : mag;
      const hp = mag - s.avg; // high-passed
      const now = Date.now();
      if (hp > 2.2 && now - s.lastPeak > 300) { s.lastPeak = now; setSteps((v) => v + 1); }
      s.window.push(Math.abs(hp)); if (s.window.length > 20) s.window.shift();
      const energy = s.window.reduce((x, y) => x + y, 0) / s.window.length;
      setMotion(energy < 0.6 ? "Still" : energy < 2 ? "Walking" : "Active");
    };
    window.addEventListener("devicemotion", onMotion);
    return () => window.removeEventListener("devicemotion", onMotion);
  }, [running]);

  const start = async () => {
    try {
      if (typeof DeviceMotionEvent !== "undefined" && typeof DeviceMotionEvent.requestPermission === "function") {
        const p = await DeviceMotionEvent.requestPermission();
        if (p !== "granted") { toast.error("Motion permission is needed to count steps."); return; }
      }
      setRunning(true);
    } catch { toast.error("Your device didn't allow motion access."); }
  };
  const stop = () => setRunning(false);
  const reset = () => setSteps(0);
  return { running, steps, motion, start, stop, reset };
}

function StreamGraph({ data, color = "#fb7185", height = 90 }) {
  const W = 600, H = height;
  if (!data || data.length < 2) {
    return (
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full" style={{ height }} data-testid="stream-graph-empty">
        <line x1="0" y1={H / 2} x2={W} y2={H / 2} stroke={color} strokeOpacity="0.25" strokeDasharray="4 6" />
      </svg>
    );
  }
  const step = W / (data.length - 1);
  const pts = data.map((v, i) => `${(i * step).toFixed(1)},${(H - 6 - v * (H - 12)).toFixed(1)}`);
  const line = pts.join(" ");
  const area = `0,${H} ${line} ${W},${H}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full" style={{ height }} data-testid="stream-graph">
      <defs>
        <linearGradient id="sgfill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={area} fill="url(#sgfill)" />
      <polyline points={line} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={W} cy={H - 6 - data[data.length - 1] * (H - 12)} r="3.5" fill={color} />
    </svg>
  );
}

const STATUS = { good: { c: "#34d399", t: "Good" }, care: { c: "#fbbf24", t: "Needs care" }, risk: { c: "#f87171", t: "See a doctor" }, none: { c: "#6b7280", t: "No data" } };
function statusOf(metric, value) {
  const n = parseFloat(value);
  if (metric === "heart_rate") { if (!n) return "none"; if (n < 40 || n > 130) return "risk"; if (n < 50 || n > 100) return "care"; return "good"; }
  if (metric === "spo2") { if (!n) return "none"; if (n < 92) return "risk"; if (n < 95) return "care"; return "good"; }
  if (metric === "bp") { const m = /(\d+)\D+(\d+)/.exec(String(value || "")); if (!m) return "none"; const s = +m[1], d = +m[2]; if (s >= 160 || d >= 100 || s < 90) return "risk"; if (s >= 130 || d >= 85) return "care"; return "good"; }
  return "none";
}

function BodyStatus({ hr, spo2, bp }) {
  const organs = [
    { key: "brain", label: "Brain / O₂", cx: 100, cy: 34, st: statusOf("spo2", spo2), val: spo2 ? `${spo2}%` : "—" },
    { key: "lungs", label: "Lungs / SpO₂", cx: 115, cy: 88, st: statusOf("spo2", spo2), val: spo2 ? `${spo2}%` : "—" },
    { key: "heart", label: "Heart / HR", cx: 86, cy: 94, st: statusOf("heart_rate", hr), val: hr ? `${hr} bpm` : "—" },
    { key: "circ", label: "Circulation / BP", cx: 100, cy: 118, st: statusOf("bp", bp), val: bp || "—" },
    { key: "kidneys", label: "Kidneys / Renal", cx: 114, cy: 144, st: statusOf("bp", bp), val: bp || "—" },
  ];
  return (
    <div className="flex flex-col items-center" data-testid="body-status">
      <svg viewBox="0 0 200 320" className="w-[170px] sm:w-[200px]" style={{ filter: "drop-shadow(0 0 22px rgba(139,92,246,0.35))" }}>
        <defs>
          <radialGradient id="bs-aura" cx="50%" cy="40%" r="55%">
            <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.28" />
            <stop offset="55%" stopColor="#22d3ee" stopOpacity="0.10" />
            <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="bs-body" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ff9d00" stopOpacity="0.22" />
            <stop offset="50%" stopColor="#a78bfa" stopOpacity="0.24" />
            <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.16" />
          </linearGradient>
          <linearGradient id="bs-edge" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#c4b5fd" />
            <stop offset="100%" stopColor="#67e8f9" />
          </linearGradient>
          <filter id="bs-glow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="1.8" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        <ellipse cx="100" cy="150" rx="92" ry="150" fill="url(#bs-aura)" />
        <g filter="url(#bs-glow)" fill="url(#bs-body)" stroke="url(#bs-edge)" strokeWidth="2" strokeLinejoin="round">
          {/* arms */}
          <path d="M58 84 L36 176" fill="none" strokeWidth="15" strokeLinecap="round" strokeOpacity="0.55" />
          <path d="M142 84 L164 176" fill="none" strokeWidth="15" strokeLinecap="round" strokeOpacity="0.55" />
          {/* legs */}
          <path d="M87 184 L83 306" fill="none" strokeWidth="24" strokeLinecap="round" strokeOpacity="0.6" />
          <path d="M113 184 L117 306" fill="none" strokeWidth="24" strokeLinecap="round" strokeOpacity="0.6" />
          {/* head + neck */}
          <circle cx="100" cy="34" r="22" />
          <rect x="90" y="50" width="20" height="18" rx="9" />
          {/* shoulders + torso */}
          <circle cx="62" cy="82" r="15" stroke="none" />
          <circle cx="138" cy="82" r="15" stroke="none" />
          <path d="M58 78 Q100 64 142 78 L130 170 Q130 187 113 189 L87 189 Q70 187 70 170 Z" />
        </g>
        {organs.map((o) => (
          <g key={o.key}>
            <circle cx={o.cx} cy={o.cy} r="11" fill={STATUS[o.st].c} opacity="0.18">
              {o.st === "risk" && <animate attributeName="r" values="11;16;11" dur="1.1s" repeatCount="indefinite" />}
            </circle>
            <circle cx={o.cx} cy={o.cy} r="5.6" fill={STATUS[o.st].c} stroke="#fff" strokeWidth="1.1" style={{ filter: `drop-shadow(0 0 6px ${STATUS[o.st].c})` }}>
              {o.st === "risk" && <animate attributeName="opacity" values="1;0.45;1" dur="1.1s" repeatCount="indefinite" />}
            </circle>
          </g>
        ))}
      </svg>
      <div className="w-full space-y-1.5 mt-2">
        {organs.map((o) => (
          <div key={o.key} className="flex items-center gap-2 text-xs" data-testid={`organ-${o.key}`}>
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: STATUS[o.st].c, boxShadow: `0 0 6px ${STATUS[o.st].c}` }} />
            <span className="flex-1 truncate">{o.label}</span>
            <span className="text-sec">{o.val}</span>
            <span style={{ color: STATUS[o.st].c }} className="font-medium">{STATUS[o.st].t}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3 mt-3 text-[10px] text-sec" data-testid="status-legend">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: STATUS.good.c }} /> Good</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: STATUS.care.c }} /> Needs care</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: STATUS.risk.c }} /> See a doctor</span>
      </div>
    </div>
  );
}

function TwinChip({ icon: Icon, label, value, unit, live, accent, testid }) {
  return (
    <div className="rud-card rounded-xl px-3 py-2.5 flex items-center gap-2.5" data-testid={testid}>
      <span className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${accent || "bg-brand/15 text-brand"}`}><Icon size={15} /></span>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-sec flex items-center gap-1">{label}{live && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}</div>
        <div className="text-lg leading-none font-serif">{value ?? "—"}{value != null && value !== "" && unit ? <span className="text-[11px] text-sec ml-1">{unit}</span> : null}</div>
      </div>
    </div>
  );
}

function TwinHero({ latest, hr, hrLive, spo2, steps, camState, wave, camBpm }) {
  const L = (t) => latest?.[t]?.value;
  return (
    <div className="rud-card rounded-2xl p-4 overflow-hidden" data-testid="digital-twin">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-3">
        <Activity size={14} /> Your Digital Twin
      </div>
      <div className="grid md:grid-cols-[1fr_auto_1fr] gap-3 md:gap-4 items-center">
        <div className="space-y-2.5 order-2 md:order-1">
          <TwinChip testid="twin-hr" icon={Heart} label="Heart rate" value={hr ?? L("heart_rate")} unit="bpm" live={hrLive} accent="bg-rose-500/15 text-rose-400" />
          <TwinChip testid="twin-spo2" icon={Activity} label="SpO₂" value={spo2 ?? L("spo2")} unit="%" accent="bg-sky-500/15 text-sky-400" />
          <TwinChip testid="twin-steps" icon={Footprints} label="Steps (session)" value={steps || L("steps")} unit="" live={steps > 0} accent="bg-emerald-500/15 text-emerald-400" />
        </div>

        <div className="order-1 md:order-2 relative flex flex-col items-center">
          <BodyStatus hr={hr ?? L("heart_rate")} spo2={spo2 ?? L("spo2")} bp={L("bp")} />
          {camState === "live" && (
            <div className="w-full mt-1" data-testid="twin-live-graph">
              <StreamGraph data={wave} color="#fb7185" height={70} />
              <div className="text-center text-[11px] text-rose-400 flex items-center justify-center gap-1.5"><Heart size={12} className="animate-pulse" /> Live pulse {camBpm ? `· ${camBpm} bpm` : "· reading…"}</div>
            </div>
          )}
        </div>

        <div className="space-y-2.5 order-3">
          <TwinChip testid="twin-bp" icon={Gauge} label="Blood pressure" value={L("bp")} unit="mmHg" accent="bg-amber-500/15 text-amber-400" />
          <TwinChip testid="twin-temp" icon={Thermometer} label="Temperature" value={L("temperature")} unit="°C" accent="bg-orange-500/15 text-orange-400" />
          <TwinChip testid="twin-weight" icon={Scale} label="Weight" value={L("weight")} unit="kg" accent="bg-violet-500/15 text-violet-400" />
        </div>
      </div>
    </div>
  );
}


const VitalCard = ({ icon: Icon, label, value, unit, sub, accent, children, testid }) => (
  <div className="rud-card rounded-2xl p-4 flex flex-col" data-testid={testid}>
    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-sec">
      <Icon size={14} className={accent || "text-brand"} /> {label}
    </div>
    <div className="mt-2 flex items-baseline gap-1.5">
      <span className="font-serif text-3xl leading-none">{value}</span>
      {unit && <span className="text-sm text-sec">{unit}</span>}
    </div>
    {sub && <div className="text-[11px] text-sec mt-1">{sub}</div>}
    {children && <div className="mt-3">{children}</div>}
  </div>
);

const SVITALS = [
  { key: "heart_rate", label: "Heart Rate", icon: Heart, unit: "bpm", base: 80, lo: 60, hi: 100, dmin: 48, dmax: 116, dec: 0 },
  { key: "systolic", label: "Systolic BP", icon: Activity, unit: "mmHg", base: 105, lo: 90, hi: 120, dmin: 84, dmax: 132, dec: 0 },
  { key: "diastolic", label: "Diastolic BP", icon: Activity, unit: "mmHg", base: 72, lo: 60, hi: 80, dmin: 54, dmax: 92, dec: 0 },
  { key: "spo2", label: "SpO₂", icon: Droplets, unit: "%", base: 98, lo: 95, hi: 100, dmin: 90, dmax: 100, dec: 0 },
  { key: "temperature", label: "Temperature", icon: Thermometer, unit: "°C", base: 36.7, lo: 36.1, hi: 37.2, dmin: 35.8, dmax: 37.8, dec: 1 },
  { key: "respiratory", label: "Respiratory Rate", icon: Wind, unit: "br/min", base: 16, lo: 12, hi: 20, dmin: 10, dmax: 24, dec: 0 },
  { key: "glucose", label: "Glucose", icon: Droplets, unit: "mg/dL", base: 108, lo: 70, hi: 140, dmin: 66, dmax: 156, dec: 0 },
];
const vStatus = (v, val) => (val < v.lo ? { t: "LOW", c: "#fbbf24" } : val > v.hi ? { t: "HIGH", c: "#f87171" } : { t: "NORMAL", c: "#34d399" });

function StreamingVitals({ onSnapshot }) {
  const [live, setLive] = useState(true);
  const [data, setData] = useState(() => { const s = {}; SVITALS.forEach((v) => { s[v.key] = { cur: v.base, hist: Array.from({ length: 26 }, () => v.base) }; }); return s; });
  const [metric, setMetric] = useState("heart_rate");
  const liveRef = useRef(live); liveRef.current = live;
  useEffect(() => {
    const id = setInterval(() => {
      if (!liveRef.current) return;
      setData((prev) => {
        const next = {};
        SVITALS.forEach((v) => {
          const p = prev[v.key];
          const drift = (v.base - p.cur) * 0.09;
          const noise = (Math.random() - 0.5) * (v.hi - v.lo) * 0.3;
          let val = p.cur + drift + noise;
          val = Math.max(v.dmin, Math.min(v.dmax, val));
          next[v.key] = { cur: val, hist: [...p.hist.slice(-25), val] };
        });
        return next;
      });
    }, 1200);
    return () => clearInterval(id);
  }, []);

  const out = SVITALS.filter((v) => { const st = vStatus(v, data[v.key].cur); return st.t !== "NORMAL"; });
  const trendV = SVITALS.find((v) => v.key === metric);
  const trend = Array.from({ length: 7 }, (_, i) => trendV.base + Math.sin(i * 1.1) * (trendV.hi - trendV.lo) * 0.28 + (Math.random() - 0.5) * 2);

  return (
    <div className="space-y-3" data-testid="streaming-vitals">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><Activity size={14} /> Live vitals — streaming (simulated)</div>
        <button data-testid="vitals-pause" onClick={() => setLive((l) => !l)} className="btn-ghost rounded-full h-8 px-3 text-xs border border-rud flex items-center gap-1.5">
          <span className={`w-1.5 h-1.5 rounded-full ${live ? "bg-emerald-400 animate-pulse" : "bg-sec"}`} /> {live ? "Pause live" : "Resume live"}
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {SVITALS.map((v) => {
          const cur = data[v.key].cur;
          const st = vStatus(v, cur);
          const norm = data[v.key].hist.map((x) => Math.max(0, Math.min(1, (x - v.dmin) / (v.dmax - v.dmin))));
          return (
            <div key={v.key} className="rud-card rounded-2xl p-4" data-testid={`svital-${v.key}`} style={{ borderColor: `${st.c}55` }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-sec"><v.icon size={14} style={{ color: st.c }} /> {v.label}</div>
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full flex items-center gap-1" style={{ color: st.c, background: `${st.c}1f` }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: st.c }} /> {st.t}</span>
              </div>
              <div className="flex items-end justify-between gap-2 mt-2">
                <div className="flex items-baseline gap-1">
                  <span className="font-serif text-3xl leading-none" style={{ color: st.c }} data-testid={`svital-val-${v.key}`}>{cur.toFixed(v.dec)}</span>
                  <span className="text-xs text-sec">{v.unit}</span>
                </div>
                <div className="w-1/2"><StreamGraph data={norm} color={st.c} height={40} /></div>
              </div>
              <div className="text-[11px] text-sec mt-1">Baseline {v.base} · normal {v.lo}–{v.hi}</div>
            </div>
          );
        })}
        {/* Guidance */}
        <div className="rud-card rounded-2xl p-4 flex flex-col" data-testid="vitals-guidance" style={{ borderColor: out.length ? "#f8717155" : "#34d39955" }}>
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-brand"><Sparkles size={14} /> Today's guidance</div>
          <p className="text-sm mt-2 flex-1">{out.length === 0
            ? "All tracked vitals are within a healthy range today. Keep up your movement, hydration and sleep. 🌱"
            : `Watch your ${out.map((v) => v.label).join(", ")} — currently outside your normal range. Rest, hydrate and re-check; consult a professional if it persists.`}</p>
          <button data-testid="vitals-snapshot" onClick={() => onSnapshot(data)} className="btn-ghost rounded-full h-9 px-4 text-sm border border-brand/40 text-brand mt-3 flex items-center justify-center gap-1.5"><ImageIcon size={14} /> Save current snapshot</button>
        </div>
      </div>
      {/* 7-day trend */}
      <div className="rud-card rounded-2xl p-4" data-testid="vitals-trend">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-brand"><TrendingUp size={14} /> Twin vs baseline — 7-day trend</div>
          <select data-testid="trend-metric" value={metric} onChange={(e) => setMetric(e.target.value)} className="rud-input rounded-lg px-2 py-1.5 text-xs">
            {SVITALS.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
          </select>
        </div>
        <div className="mt-3 flex items-end gap-2 h-28">
          {trend.map((val, i) => {
            const st = vStatus(trendV, val);
            const h = Math.max(8, Math.min(100, ((val - trendV.dmin) / (trendV.dmax - trendV.dmin)) * 100));
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full rounded-t-md transition-all" style={{ height: `${h}%`, background: `linear-gradient(to top, ${st.c}, ${st.c}66)` }} title={val.toFixed(trendV.dec)} />
                <span className="text-[9px] text-sec">D{i + 1}</span>
              </div>
            );
          })}
        </div>
        <div className="text-[11px] text-sec mt-1">Baseline {trendV.base} {trendV.unit} · dashed = your personal baseline</div>
      </div>
    </div>
  );
}

function StepsLeaderboard() {
  const [period, setPeriod] = useState("week");
  const [rows, setRows] = useState([]);
  const [me, setMe] = useState({ rank: null, steps: 0 });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let on = true; setLoading(true);
    api.get(`/vitals/steps/leaderboard?period=${period}`).then(({ data }) => {
      if (!on) return; setRows(data.leaderboard || []); setMe({ rank: data.my_rank, steps: data.my_steps || 0 });
    }).catch(() => {}).finally(() => on && setLoading(false));
    return () => { on = false; };
  }, [period]);
  const medal = (r) => (r === 1 ? "#fbbf24" : r === 2 ? "#cbd5e1" : r === 3 ? "#d97706" : null);
  return (
    <div className="rud-card rounded-2xl p-4" data-testid="steps-leaderboard">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-brand"><Trophy size={14} /> Step Leaderboard</div>
        <div className="flex gap-1">
          {["today", "week", "all"].map((p) => (
            <button key={p} data-testid={`lb-period-${p}`} onClick={() => setPeriod(p)}
              className={`text-[11px] rounded-full px-2.5 py-1 border transition-colors ${period === p ? "bg-brand text-black border-brand" : "border-rud text-sec hover:border-brand"}`}>
              {p === "today" ? "Today" : p === "week" ? "This week" : "All time"}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="py-6 text-center text-sec"><Loader2 size={16} className="animate-spin mx-auto" /></div>
      ) : rows.length === 0 ? (
        <p className="py-5 text-center text-sec text-sm" data-testid="lb-empty">No steps logged yet — start the step counter and hit Save to join the board. 👟</p>
      ) : (
        <div className="mt-3 space-y-1.5">
          {rows.map((r) => (
            <div key={r.user_id} data-testid={`lb-row-${r.rank}`} className={`flex items-center gap-3 rounded-xl px-3 py-2 ${r.is_me ? "bg-brand/10 border border-brand/30" : "bg-white/5"}`}>
              <span className="w-6 text-center font-serif text-sm" style={{ color: medal(r.rank) || undefined }}>
                {medal(r.rank) ? <Medal size={15} className="inline" style={{ color: medal(r.rank) }} /> : r.rank}
              </span>
              <div className="w-8 h-8 rounded-full bg-brand/15 text-brand grid place-items-center text-xs font-bold overflow-hidden shrink-0">
                {r.avatar_url ? <img src={r.avatar_url} alt="" className="w-full h-full object-cover" /> : (r.name?.[0] || "?").toUpperCase()}
              </div>
              <span className="flex-1 truncate text-sm">{r.name}{r.is_me && <span className="text-[10px] text-brand ml-1">· you</span>}</span>
              <span className="font-serif text-brand">{r.steps.toLocaleString()}</span>
            </div>
          ))}
          {me.rank == null && <p className="text-[11px] text-sec pt-1">You're not on the board yet — save some steps to join. 👟</p>}
        </div>
      )}
    </div>
  );
}

export default function LiveVitals() {
  const step = useStepCounter();
  const [latest, setLatest] = useState({});
  const [history, setHistory] = useState([]);

  // Bluetooth HR
  const [btBpm, setBtBpm] = useState(null);
  const [btName, setBtName] = useState("");

  // Camera pulse (live streaming)
  const [camState, setCamState] = useState("idle"); // idle | live
  const [camBpm, setCamBpm] = useState(null);
  const [wave, setWave] = useState([]);
  const videoRef = useRef(null);
  const camRefs = useRef({ stream: null, raf: 0, samples: [], t0: 0 });

  const load = async () => {
    try { const { data } = await api.get("/vitals"); setLatest(data.latest || {}); setHistory(data.readings || []); } catch { /* */ }
  };
  useEffect(() => { load(); return () => stopCamera(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (camBpm) checkVitalRisk("heart_rate", camBpm); /* eslint-disable-next-line */ }, [camBpm]);
  useEffect(() => { if (btBpm) checkVitalRisk("heart_rate", btBpm); /* eslint-disable-next-line */ }, [btBpm]);

  const riskRef = useRef(0);
  const checkVitalRisk = (metric, value) => {
    let risk = false, why = "";
    const n = parseFloat(value);
    if (metric === "heart_rate" && n) { if (n < 40 || n > 130) { risk = true; why = `heart rate ${n} bpm`; } }
    if (metric === "spo2" && n) { if (n < 92) { risk = true; why = `SpO₂ ${n}%`; } }
    if (metric === "bp") { const m = /(\d+)\D+(\d+)/.exec(String(value)); if (m) { const s = +m[1], d = +m[2]; if (s >= 180 || d >= 120 || s < 90) { risk = true; why = `BP ${s}/${d}`; } } }
    if (risk && Date.now() - riskRef.current > 60000) {
      riskRef.current = Date.now();
      toast.error(`Abnormal reading (${why}) — alerting your Guardian`);
      window.dispatchEvent(new CustomEvent("mykind-vital-risk", { detail: { reason: "vital" } }));
    }
  };

  const save = async (type, value, unit = "", meta = {}) => {
    try { await api.post("/vitals", { type, value: String(value), unit, meta }); toast.success("Saved to your vitals log"); load(); checkVitalRisk(type, value); }
    catch { toast.error("Couldn't save — please try again."); }
  };

  const saveSnapshot = async (data) => {
    try {
      const jobs = [
        ["heart_rate", data.heart_rate.cur.toFixed(0), "bpm"],
        ["bp", `${data.systolic.cur.toFixed(0)}/${data.diastolic.cur.toFixed(0)}`, "mmHg"],
        ["spo2", data.spo2.cur.toFixed(0), "%"],
        ["temperature", data.temperature.cur.toFixed(1), "°C"],
        ["glucose", data.glucose.cur.toFixed(0), "mg/dL"],
      ];
      await Promise.all(jobs.map(([type, value, unit]) => api.post("/vitals", { type, value: String(value), unit, meta: { source: "snapshot" } })));
      toast.success("Snapshot saved to your vitals log 📸");
      load();
    } catch { toast.error("Couldn't save the snapshot — please try again."); }
  };

  // ---- Bluetooth heart-rate monitor ----
  const connectBluetooth = async () => {
    if (!navigator.bluetooth) { toast.error("This browser doesn't support Bluetooth. Try Chrome on Android/desktop."); return; }
    try {
      const device = await navigator.bluetooth.requestDevice({ filters: [{ services: ["heart_rate"] }], optionalServices: ["battery_service"] });
      setBtName(device.name || "Monitor");
      const server = await device.gatt.connect();
      const service = await server.getPrimaryService("heart_rate");
      const ch = await service.getCharacteristic("heart_rate_measurement");
      await ch.startNotifications();
      ch.addEventListener("characteristicvaluechanged", (e) => {
        const v = e.target.value; const flags = v.getUint8(0);
        const hr = (flags & 1) ? v.getUint16(1, true) : v.getUint8(1);
        if (hr) setBtBpm(hr);
      });
      device.addEventListener("gattserverdisconnected", () => { setBtName(""); setBtBpm(null); });
      toast.success(`Connected to ${device.name || "monitor"}`);
    } catch (e) { if (e?.name !== "NotFoundError") toast.error("Couldn't connect. Make sure your monitor is on and nearby."); }
  };

  // ---- Camera-based pulse (PPG) ----
  const stopCamera = () => {
    const r = camRefs.current;
    if (r.raf) cancelAnimationFrame(r.raf);
    if (r.stream) r.stream.getTracks().forEach((t) => t.stop());
    camRefs.current = { stream: null, raf: 0, samples: [], t0: 0 };
  };

  const measureCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) { toast.error("Camera isn't available in this browser."); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } } });
      camRefs.current.stream = stream;
      const track = stream.getVideoTracks()[0];
      try { await track.applyConstraints({ advanced: [{ torch: true }] }); } catch { /* torch not supported */ }
      const video = videoRef.current;
      video.srcObject = stream; await video.play();
      setCamState("live"); setCamBpm(null); setWave([]);
      const canvas = document.createElement("canvas"); canvas.width = 64; canvas.height = 64;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      camRefs.current.samples = []; camRefs.current.t0 = performance.now();
      const WINDOW_MS = 8000;   // rolling analysis window
      let lastCalc = 0, lastWave = 0;
      const tick = () => {
        const r = camRefs.current; if (!r.stream) return;
        const now = performance.now();
        try {
          ctx.drawImage(video, 0, 0, 64, 64);
          const d = ctx.getImageData(20, 20, 24, 24).data;
          let red = 0, n = 0; for (let i = 0; i < d.length; i += 4) { red += d[i]; n++; }
          r.samples.push({ t: now, v: red / n });
        } catch { /* frame not ready */ }
        // keep only the rolling window
        while (r.samples.length && now - r.samples[0].t > WINDOW_MS) r.samples.shift();
        // stream the waveform ~12fps for a smooth scrolling graph
        if (now - lastWave > 80 && r.samples.length > 10) {
          lastWave = now;
          const recent = r.samples.slice(-140).map((x) => x.v);
          const mn = Math.min(...recent), mx = Math.max(...recent);
          setWave(recent.map((v) => (mx > mn ? (v - mn) / (mx - mn) : 0.5)));
        }
        // recompute a live BPM ~ every 1s once we have enough signal
        if (now - lastCalc > 1000 && r.samples.length > 60) {
          lastCalc = now;
          const bpm = computeBpm(r.samples);
          if (bpm) setCamBpm(bpm);
        }
        r.raf = requestAnimationFrame(tick);
      };
      camRefs.current.raf = requestAnimationFrame(tick);
    } catch { toast.error("Couldn't open the camera. Please allow camera access."); setCamState("idle"); }
  };

  const stopCameraStream = () => { stopCamera(); setCamState("idle"); setWave([]); };

  const computeBpm = (samplesRaw) => {
    const s = samplesRaw.slice();
    if (s.length < 60) return null;
    const vals = s.map((x) => x.v);
    const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
    const variance = vals.reduce((a, b) => a + (b - mean) ** 2, 0) / vals.length;
    if (variance < 1.2) return null; // finger not placed / weak signal
    const sm = vals.map((_, i) => { const w = vals.slice(Math.max(0, i - 3), i + 4); return w.reduce((a, b) => a + b, 0) / w.length; });
    const base = sm.map((_, i) => { const w = sm.slice(Math.max(0, i - 15), i + 16); return w.reduce((a, b) => a + b, 0) / w.length; });
    const sig = sm.map((v, i) => v - base[i]);
    let peaks = 0, lastPeakT = 0;
    for (let i = 2; i < sig.length - 2; i++) {
      if (sig[i] > 0 && sig[i] > sig[i - 1] && sig[i] >= sig[i + 1]) {
        const t = s[i].t;
        if (t - lastPeakT > 350) { peaks++; lastPeakT = t; }
      }
    }
    const durSec = (s[s.length - 1].t - s[0].t) / 1000;
    if (durSec < 3 || peaks < 3) return null;
    const bpm = Math.round((peaks / durSec) * 60);
    return (bpm >= 40 && bpm <= 200) ? bpm : null;
  };

  const QUICK = [
    { type: "heart_rate", label: "Heart rate", unit: "bpm", icon: Heart, ph: "e.g. 72" },
    { type: "spo2", label: "SpO₂", unit: "%", icon: Activity, ph: "e.g. 98" },
    { type: "bp", label: "Blood pressure", unit: "mmHg", icon: Gauge, ph: "e.g. 120/80" },
    { type: "temperature", label: "Temperature", unit: "°C", icon: Thermometer, ph: "e.g. 36.8" },
    { type: "weight", label: "Weight", unit: "kg", icon: Scale, ph: "e.g. 70" },
    { type: "glucose", label: "Blood glucose", unit: "mg/dL", icon: Droplets, ph: "e.g. 95" },
    { type: "sleep", label: "Sleep", unit: "hrs", icon: Moon, ph: "e.g. 7.5" },
    { type: "water", label: "Water", unit: "glasses", icon: Droplets, ph: "e.g. 6" },
    { type: "mood", label: "Mood", unit: "", icon: Smile, ph: "e.g. Calm" },
  ];
  const [qType, setQType] = useState("heart_rate");
  const [qVal, setQVal] = useState("");
  const active = QUICK.find((q) => q.type === qType);
  const submitQuick = () => { if (!qVal.trim()) { toast.error("Enter a value first."); return; } save(qType, qVal.trim(), active.unit); setQVal(""); };

  return (
    <div className="space-y-4" data-testid="live-vitals">
      <div className="flex items-start gap-2 rounded-xl bg-elevated border border-brand/25 p-3 text-xs" data-testid="vitals-disclaimer">
        <AlertTriangle size={16} className="shrink-0 mt-0.5 text-brand" />
        <span>Live Vitals is for <b className="text-[var(--rud-text)]">awareness & decision-support only — it is not a medical device and does not provide a diagnosis.</b> Readings from a phone sensor or camera are estimates. For any health concern, consult a licensed professional.</span>
      </div>

      {/* Digital Twin hero */}
      <TwinHero latest={latest} hr={camBpm ?? btBpm} hrLive={camState === "live" || !!btBpm}
        spo2={undefined} steps={step.steps} camState={camState} wave={wave} camBpm={camBpm} />

      {/* Streaming multi-vital dashboard */}
      <StreamingVitals onSnapshot={saveSnapshot} />

      {/* Step leaderboard */}
      <StepsLeaderboard />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {/* Steps */}
        <VitalCard icon={Footprints} label="Steps (session)" value={step.steps} sub={`Movement: ${step.motion}`} testid="vital-steps">
          <div className="flex gap-2">
            {!step.running
              ? <button data-testid="steps-start" onClick={step.start} className="btn-primary rounded-full h-8 px-4 text-xs">Start counting</button>
              : <button data-testid="steps-stop" onClick={step.stop} className="btn-ghost rounded-full h-8 px-4 text-xs border border-rud">Stop</button>}
            {step.steps > 0 && (
              <>
                <button data-testid="steps-reset" onClick={step.reset} className="btn-ghost rounded-full h-8 px-3 text-xs border border-rud">Reset</button>
                <button data-testid="steps-save" onClick={() => save("steps", step.steps, "steps")} className="btn-ghost rounded-full h-8 px-3 text-xs border border-brand/40 text-brand">Save</button>
              </>
            )}
          </div>
        </VitalCard>

        {/* Camera pulse (live) */}
        <VitalCard icon={Camera} label="Heart rate · camera" value={camBpm ?? "—"} unit={camBpm ? "bpm" : ""} accent="text-rose-400"
          sub={camState === "live" ? "Live · fingertip on rear camera + flash" : "Fingertip on rear camera + flash"} testid="vital-camera">
          <video ref={videoRef} playsInline muted
            style={camState === "live"
              ? { width: 72, height: 72, borderRadius: 12, objectFit: "cover", marginBottom: 8 }
              : { width: 2, height: 2, opacity: 0.01, position: "absolute", pointerEvents: "none" }} />
          {camState === "live"
            ? <div className="flex gap-2">
                <button data-testid="camera-stop" onClick={stopCameraStream} className="btn-ghost rounded-full h-8 px-4 text-xs border border-rud flex items-center gap-1.5"><Loader2 size={13} className="animate-spin" /> Stop</button>
                {camBpm && <button data-testid="camera-save" onClick={() => save("heart_rate", camBpm, "bpm", { source: "camera" })} className="btn-ghost rounded-full h-8 px-3 text-xs border border-brand/40 text-brand">Save</button>}
              </div>
            : <button data-testid="camera-measure" onClick={measureCamera} className="btn-primary rounded-full h-8 px-4 text-xs">Start live</button>}
          <span className="block text-[10px] text-sec mt-2">Experimental · shows a live streaming graph above</span>
        </VitalCard>

        {/* Bluetooth */}
        <VitalCard icon={Bluetooth} label="Bluetooth monitor" value={btBpm ?? "—"} unit={btBpm ? "bpm" : ""} accent="text-sky-400"
          sub={btName ? `Connected: ${btName}` : "Pair a BLE heart-rate / SpO₂ device"} testid="vital-bluetooth">
          <div className="flex gap-2">
            <button data-testid="bt-connect" onClick={connectBluetooth} className="btn-primary rounded-full h-8 px-4 text-xs">{btName ? "Reconnect" : "Connect device"}</button>
            {btBpm && <button data-testid="bt-save" onClick={() => save("heart_rate", btBpm, "bpm", { source: "bluetooth" })} className="btn-ghost rounded-full h-8 px-3 text-xs border border-brand/40 text-brand">Save</button>}
          </div>
        </VitalCard>
      </div>

      {/* Guardian — fall & accident alerts */}
      <Guardian />

      {/* Quick log */}
      <div className="rud-card rounded-2xl p-4" data-testid="vitals-quicklog">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-3"><Plus size={14} /> Log a vital</div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {QUICK.map((q) => (
            <button key={q.type} data-testid={`qtype-${q.type}`} onClick={() => { setQType(q.type); setQVal(""); }}
              className={`text-[11px] rounded-full px-3 py-1.5 border transition-colors flex items-center gap-1.5 ${qType === q.type ? "bg-brand text-black border-brand" : "border-rud text-sec hover:border-brand"}`}>
              <q.icon size={12} /> {q.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <input data-testid="quick-value" value={qVal} onChange={(e) => setQVal(e.target.value)} placeholder={active?.ph}
            onKeyDown={(e) => e.key === "Enter" && submitQuick()}
            className="rud-input flex-1 rounded-lg px-3 py-2.5 text-sm" />
          {active?.unit && <span className="self-center text-xs text-sec w-16">{active.unit}</span>}
          <button data-testid="quick-save" onClick={submitQuick} className="btn-primary rounded-lg px-5 text-sm">Save</button>
        </div>
      </div>

      {/* Recent history */}
      {history.length > 0 && (
        <div className="rud-card rounded-2xl p-4" data-testid="vitals-history">
          <div className="text-xs uppercase tracking-widest text-sec mb-3">Recent readings</div>
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {history.slice(0, 40).map((r) => (
              <div key={r.id} className="flex items-center justify-between text-sm border-b border-rud/50 pb-1.5" data-testid={`vital-row-${r.id}`}>
                <span className="capitalize text-[var(--rud-text)]">{r.type.replace(/_/g, " ")}{r.meta?.source ? <span className="text-[10px] text-sec ml-1.5">· {r.meta.source}</span> : null}</span>
                <span className="flex items-center gap-3">
                  <b className="text-brand">{r.value}{r.unit ? ` ${r.unit}` : ""}</b>
                  <span className="text-[11px] text-sec">{fmtTime(r.ts)}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
